import { useState, useCallback, useEffect, useRef } from 'react';

declare global {
  interface Window {
    documentPictureInPicture?: {
      requestWindow(options?: { width?: number; height?: number }): Promise<Window>;
      window: Window | null;
    };
  }
}

interface UsePictureInPictureOptions {
  onLeave?: () => void;
}

interface MovedElement {
  el: HTMLElement;
  originalParent: HTMLElement;
  originalNextSibling: Node | null;
}

const PIP_STYLES = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body {
    background: #171717;
    overflow: hidden;
    width: 100vw;
    height: 100vh;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
  }
  #pip-remote {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
  #pip-local {
    position: absolute;
    bottom: 12px;
    right: 12px;
    width: 120px;
    height: 68px;
    object-fit: cover;
    border-radius: 8px;
    border: 1px solid rgba(255,255,255,0.3);
  }
`;

const moveElementToPiP = (
  el: HTMLElement,
  pipWindow: Window,
  newId: string,
  prepend = false
): MovedElement => {
  const originalParent = el.parentElement!;
  const originalNextSibling = el.nextSibling;
  el.id = newId;
  if (prepend) {
    pipWindow.document.body.prepend(el);
  } else {
    pipWindow.document.body.appendChild(el);
  }
  return { el, originalParent, originalNextSibling };
};

const restoreElement = ({ el, originalParent, originalNextSibling }: MovedElement) => {
  if (originalNextSibling) {
    originalParent.insertBefore(el, originalNextSibling);
  } else {
    originalParent.appendChild(el);
  }
};

export const usePictureInPicture = ({ onLeave }: UsePictureInPictureOptions = {}) => {
  const [isPiP, setIsPiP] = useState(false);
  const onLeaveRef = useRef(onLeave);
  onLeaveRef.current = onLeave;

  const pipWindowRef = useRef<Window | null>(null);
  const movedElementsRef = useRef<MovedElement[]>([]);
  const remoteVideoInPiPRef = useRef<HTMLVideoElement | null>(null);

  const isSupported = typeof window !== 'undefined' && 'documentPictureInPicture' in window;

  const restoreAllElements = useCallback(() => {
    movedElementsRef.current.forEach(restoreElement);
    movedElementsRef.current = [];
  }, []);

  const swapRemoteVideo = useCallback((newRemote: HTMLVideoElement) => {
    const pipWindow = pipWindowRef.current;
    if (!pipWindow || remoteVideoInPiPRef.current === newRemote) return;

    const oldRemote = remoteVideoInPiPRef.current;
    if (oldRemote) {
      oldRemote.id = 'remote-video';
      const entry = movedElementsRef.current.find((m) => m.el === oldRemote);
      if (entry) {
        restoreElement(entry);
        movedElementsRef.current = movedElementsRef.current.filter((m) => m.el !== oldRemote);
      }
    }

    const moved = moveElementToPiP(newRemote, pipWindow, 'pip-remote', true);
    movedElementsRef.current.push(moved);
    remoteVideoInPiPRef.current = newRemote;
  }, []);

  const enterPiP = useCallback(async () => {
    if (!isSupported || !window.documentPictureInPicture) return;

    const remoteVideo = document.getElementById('remote-video') as HTMLVideoElement | null;
    const localVideo = document.getElementById('local-video') as HTMLVideoElement | null;
    if (!remoteVideo && !localVideo) return;

    try {
      const pipWindow = await window.documentPictureInPicture.requestWindow({
        width: 480,
        height: 270,
      });
      pipWindowRef.current = pipWindow;

      const style = pipWindow.document.createElement('style');
      style.textContent = PIP_STYLES;
      pipWindow.document.head.appendChild(style);

      const moved: MovedElement[] = [];
      if (remoteVideo) {
        moved.push(moveElementToPiP(remoteVideo, pipWindow, 'pip-remote'));
        remoteVideoInPiPRef.current = remoteVideo;
      }
      if (localVideo) {
        moved.push(moveElementToPiP(localVideo, pipWindow, 'pip-local'));
      }
      movedElementsRef.current = moved;

      pipWindow.addEventListener('pagehide', () => {
        if (remoteVideoInPiPRef.current) remoteVideoInPiPRef.current.id = 'remote-video';
        if (localVideo) localVideo.id = 'local-video';
        remoteVideoInPiPRef.current = null;
        restoreAllElements();
        pipWindowRef.current = null;
        setIsPiP(false);
        onLeaveRef.current?.();
      });

      setIsPiP(true);
    } catch (err) {
      console.warn('Failed to enter Picture-in-Picture:', err);
    }
  }, [isSupported, restoreAllElements]);

  const exitPiP = useCallback(() => {
    pipWindowRef.current?.close();
    pipWindowRef.current = null;
    restoreAllElements();
    setIsPiP(false);
  }, [restoreAllElements]);

  useEffect(() => {
    return () => {
      pipWindowRef.current?.close();
      pipWindowRef.current = null;
      restoreAllElements();
    };
  }, [restoreAllElements]);

  return {
    isPiP,
    isSupported,
    enterPiP,
    exitPiP,
    swapRemoteVideo,
  };
};
